/*
  Fills in the order-by countdown and the delivery window on the product page.

  Everything is worked out from the warehouse's wall clock rather than the visitor's, so a
  shopper in Kyiv and one in Denver are told the same cutoff. Liquid renders the wording; this
  only writes the numbers into it, which is why nothing shifts on load.

  A custom element, so the quick add popup, which re-runs the scripts of the markup it
  injects, neither defines it twice nor leaves a ticking interval behind when it closes.
*/
if (!customElements.get('shipping-countdown')) {
  customElements.define(
    'shipping-countdown',
    class ShippingCountdown extends HTMLElement {
      static DAY_MS = 24 * 60 * 60 * 1000;
      static FALLBACK_TIMEZONE = 'America/Los_Angeles';

      // Formatted in UTC because the dates below are calendar days pinned to UTC, not instants
      static dateLabel = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });

      static weekdayLabel = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long' });

      connectedCallback() {
        this.status = this.querySelector('[data-shipping-status]');
        this.international = this.querySelector('[data-shipping-international]');
        this.arrival = this.querySelector('[data-shipping-arrival]');
        this.arrivalText = this.querySelector('[data-shipping-arrival-text]');

        this.config = {
          timeZone: this.dataset.timezone || ShippingCountdown.FALLBACK_TIMEZONE,
          cutoffHour: Number(this.dataset.cutoffHour) || 12,
          transitMin: Number(this.dataset.transitMin) || 0,
          transitMax: Number(this.dataset.transitMax) || 0,
          holidays: new Set((this.dataset.holidays || '').match(/\d{4}-\d{2}-\d{2}/g) || []),
        };

        // QA hook: ?shipping_now=2026-09-18T15:00:00-07:00 renders the block as of that moment
        const override = new URLSearchParams(window.location.search).get('shipping_now');
        const parsed = override ? new Date(override) : null;
        this.offset = parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() - Date.now() : 0;

        this.resolveCountry();

        this.scope = this.closest('product-info') || document;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.scope.addEventListener('change', this.onVariantChange);

        this.render();
        this.timer = window.setInterval(() => this.render(), 1000);
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
        if (this.scope) this.scope.removeEventListener('change', this.onVariantChange);
      }

      onVariantChange(event) {
        const picker = event.target.closest('variant-radios, variant-selects');
        if (!picker || !picker.currentVariant) return;

        this.hidden = !picker.currentVariant.available;
      }

      // Markets has the final say; otherwise the country is looked up, and until an answer
      // arrives the block keeps the US wording rather than flashing the other one at everybody.
      resolveCountry() {
        const marketsCountry = this.dataset.marketsCountry;

        if (marketsCountry && marketsCountry !== 'US') {
          this.setOutsideUS(true);
          return;
        }

        this.detectCountry().then((country) => {
          if (country) this.setOutsideUS(country !== 'US');
        });
      }

      /*
        Shopify's own detection first. The store has no country markets, so Liquid reports every
        visitor as US, but this endpoint still knows where they are, needs no key and answers on
        preview domains. The theme's MaxMind lookup is the fallback: it is tied to registered
        domains and refuses to answer off them, which is how a shopper in Ukraine was being shown
        the US wording.
      */
      detectCountry() {
        return fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            const country = data && data.detected_values && data.detected_values.country;

            if (country && country.handle) return country.handle;

            const geo = window.theme && window.theme.getGeoCountry;
            return typeof geo === 'function' ? geo() : null;
          })
          .catch(() => {
            const geo = window.theme && window.theme.getGeoCountry;
            return typeof geo === 'function' ? geo() : null;
          });
      }

      setOutsideUS(isOutside) {
        this.status.hidden = isOutside;
        this.arrival.hidden = isOutside;
        this.international.hidden = !isOutside;
      }

      // Wall-clock time in the warehouse timezone, whatever the visitor's own clock says
      warehouseClock(now) {
        let formatter;

        try {
          formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: this.config.timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          });
        } catch (e) {
          this.config.timeZone = ShippingCountdown.FALLBACK_TIMEZONE;
          return this.warehouseClock(now);
        }

        const parts = formatter
          .formatToParts(now)
          .reduce((acc, part) => ({ ...acc, [part.type]: Number(part.value) }), {});

        return {
          // Calendar date only, pinned to UTC so adding days is not thrown off by a DST switch
          date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
          seconds: parts.hour * 3600 + parts.minute * 60 + parts.second,
        };
      }

      addDays(date, days) {
        return new Date(date.getTime() + days * ShippingCountdown.DAY_MS);
      }

      isBusinessDay(date) {
        const weekday = date.getUTCDay();

        return weekday !== 0 && weekday !== 6 && !this.config.holidays.has(date.toISOString().slice(0, 10));
      }

      nextBusinessDay(date) {
        let next = this.addDays(date, 1);

        while (!this.isBusinessDay(next)) next = this.addDays(next, 1);

        return next;
      }

      addBusinessDays(date, count) {
        let result = date;

        for (let i = 0; i < count; i += 1) result = this.nextBusinessDay(result);

        return result;
      }

      formatCountdown(seconds) {
        if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;

        return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
      }

      shippingState(now) {
        const { date: today, seconds } = this.warehouseClock(now);
        const cutoff = this.config.cutoffHour * 3600;

        if (this.isBusinessDay(today) && seconds < cutoff) {
          return { status: 'today', shipDate: today, remaining: cutoff - seconds };
        }

        const shipDate = this.nextBusinessDay(today);
        const isWeekendGap = [5, 6, 0].includes(today.getUTCDay()) && shipDate.getUTCDay() === 1;
        const isTomorrow = shipDate.getTime() === this.addDays(today, 1).getTime();

        return { status: !isWeekendGap && isTomorrow ? 'next' : 'on', shipDate };
      }

      // Writes "text [token] text" with the token in its own bold element, no innerHTML involved
      fillTemplate(target, template, token, value) {
        const [before, after = ''] = template.split(token);
        const strong = document.createElement('strong');

        strong.className = 'shipping-countdown__strong';
        strong.textContent = value;
        target.replaceChildren(before, strong, after);
      }

      render() {
        const state = this.shippingState(new Date(Date.now() + this.offset));

        if (state.status === 'today') {
          this.fillTemplate(this.status, this.dataset.textToday, '[time]', this.formatCountdown(state.remaining));
        } else if (state.status === 'next') {
          this.status.textContent = this.dataset.textNext;
        } else {
          this.status.textContent = this.dataset.textOn.replace(
            '[weekday]',
            ShippingCountdown.weekdayLabel.format(state.shipDate)
          );
        }

        const from = this.addBusinessDays(state.shipDate, this.config.transitMin);
        const to = this.addBusinessDays(state.shipDate, Math.max(this.config.transitMin, this.config.transitMax));
        const dates =
          from.getTime() === to.getTime()
            ? ShippingCountdown.dateLabel.format(from)
            : `${ShippingCountdown.dateLabel.format(from)} – ${ShippingCountdown.dateLabel.format(to)}`;

        this.fillTemplate(this.arrivalText, this.dataset.textArrives, '[dates]', dates);

        this.status.classList.remove('shipping-countdown__pending');
        this.arrival.classList.remove('shipping-countdown__pending');
      }
    }
  );
}
